import { useRef } from 'react'
import type { OfferPageFilterType, OfferPageMultiRoomAutoselectionReason, OfferPageMultiRoomMode, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import FlashMobileStickyHeaderFilters from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/FlashMobileStickyHeaderFilters'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import FlashHotelMobileSameRoomOptions from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/FlashHotelMobileSameRoomOptions'
import FlashHotelMobileIndividualRooms from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/FlashHotelMobileIndividualRooms'
import FlashPackageSelectionSection from '../../packageSelection/FlashPackageSelectionSection'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { getCanUseMultiRoomUI } from 'selectors/offerPage/offerPageSelectors'
import OfferPageRoomModeToggleSection from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageRoomModeToggleSection'
import OfferPagePackagesHeading from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPagePackagesHeading'
import OfferPageTogglesSection from 'components/OfferPage/MultipleRoomsBooking/Mobile/OfferPageTogglesSection'
import { useIntl } from 'react-intl'
import useFlashRoomModeChange from '../../packageSelection/useFlashRoomModeChange'

interface Props {
  optionD?: boolean
  rooms?: Array<App.RoomOccupants>
  activeFilter?: OfferPageFilterType
  onClose: () => void
  onBack?: () => void
  canUseMultiRoomUI: boolean
  multipleRoomBookingActive: boolean
  roomMode: OfferPageMultiRoomMode
  offer: App.HotelOffer
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason
  charterBundleRoute?: App.CharterBundleRoute
}

const defaultRooms = [generateOfferPageRoom()]

function MobileMultipleRoomsBookingSelectRoomsModal({
  optionD,
  rooms = defaultRooms,
  activeFilter,
  onClose,
  onBack,
  multipleRoomBookingActive,
  canUseMultiRoomUI,
  roomMode,
  offer,
  autoselectionReason,
  charterBundleRoute,
}: Props) {
  const onFlashModeChange = useFlashRoomModeChange()
  const intl = useIntl()
  const isConfirmStep = activeFilter === 'multiRoomConfirm'

  const modalTitle = isConfirmStep
    ? intl.formatMessage({
        id: 'hfxvN8WQ_tPDJB7mfDG9',
        defaultMessage: 'Confirm room selection',
        description: 'Title for confirm room selection modal on the hotel or room booking page',
      })
    : intl.formatMessage(
        {
          id: 'G1m1CIrbNnhqAtYgAeeM',
          defaultMessage: 'Select your {count, plural, one {# room} other {# rooms}}',
          description: 'Modal title for the room selection dialog showing available rooms for the booking',
        },
        { count: rooms.length },
      )

  const headerRef = useRef<HTMLDivElement | null>(null)

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <FlashMobileStickyHeaderFilters onAfterCloseCapacity={onClose} ref={headerRef} />
      </ModalHeader>
      <ModalBody>
        <Pane background="light-background">
          <Group direction="vertical" gap={8}>
            {canUseMultiRoomUI && <OfferPageRoomModeToggleSection
              onModeChange={optionD ? onFlashModeChange : undefined}
              roomMode={roomMode}
              rooms={rooms}
              saleUnit={offer.saleUnit}
              autoselectionReason={autoselectionReason}
              charterBundleRoute={charterBundleRoute}
              withHeading={false}
            />}
            {!canUseMultiRoomUI && <OfferPagePackagesHeading
              charterBundleRoute={charterBundleRoute}
            />}

            <OfferPageTogglesSection bundledWithFlightsOnly={offer.bundledWithFlightsOnly} />

            {multipleRoomBookingActive && <FlashHotelMobileIndividualRooms />}
            {!multipleRoomBookingActive && optionD && <FlashPackageSelectionSection />}
            {!multipleRoomBookingActive && !optionD && <FlashHotelMobileSameRoomOptions showSelection={isConfirmStep} headerRef={headerRef} />}
          </Group>
        </Pane>
      </ModalBody>
    </>
  )
}

type MappedProps = Pick<Props, 'rooms' | 'activeFilter' | 'canUseMultiRoomUI' | 'multipleRoomBookingActive' | 'roomMode' | 'offer' | 'autoselectionReason' | 'charterBundleRoute'>

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State): MappedProps => ({
  rooms: state.rooms,
  activeFilter: state.activeFilter,
  canUseMultiRoomUI: getCanUseMultiRoomUI(state, appState),
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  roomMode: state.multiRoomMode,
  offer: state.offer as App.HotelOffer,
  autoselectionReason: state.multiRoomAutoselectionReason,
  charterBundleRoute: state.charterBundleRoute,
}), MobileMultipleRoomsBookingSelectRoomsModal)
